import { LangCode } from '@/components/i18n/lang-context'
import { Author, buildAuthorUrl } from '@/components/jbda/author'
import { DEFAULT_HYGRAPH_LANG } from '@/lib/utils/utils'

// Normalizes string, removes diacritics, and escapes vCard special characters.
const sanitizeVCardTextValue = (text: string): string => {
  if (!text) return ''
  // Step 1: Normalize to NFD (decomposed form) and remove diacritics (accents)
  const normalizedText = text.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  // Step 2: Escape vCard special characters: backslash, comma, semicolon, newline
  return normalizedText.replace(/\\/g, '\\\\').replace(/,/g, '\\,').replace(/;/g, '\\;').replace(/\n/g, '\\n')
}

// Original escapeVCardText is kept for fields that do not need diacritic removal
// but might contain vCard special characters (e.g., phone, email if they could have newlines).
// However, for phone and email, such characters are unlikely and often stripped by other formatters.
// For simplicity, we will use sanitizeVCardTextValue for N, FN, TITLE and assume phone/email are clean
// or use a simpler escape if absolutely necessary.
// For now, let's assume phone/email don't need complex escaping beyond what sanitizeVCardTextValue does if we were to apply it.
// Let's make escapeVCardText available and use sanitizeVCardTextValue for fields with potential accents.
const escapeVCardText = (text: string): string => {
  if (!text) return ''
  return text.replace(/\\/g, '\\\\').replace(/,/g, '\\,').replace(/;/g, '\\;').replace(/\n/g, '\\n')
}

const imageToRawBase64 = async (imageUrl: string | undefined | null): Promise<{ base64: string | null, type: string | null }> => {
  if (!imageUrl) return { base64: null, type: null }
  try {
    const response = await fetch(imageUrl)
    if (!response.ok) {
      console.error(`Failed to fetch image ${imageUrl}: ${response.statusText}`)
      return { base64: null, type: null }
    }
    const blob = await response.blob()
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onloadend = () => {
        const dataUrl = reader.result as string
        const base64Data = dataUrl.split(',')[1]
        const mimeType = dataUrl.substring(dataUrl.indexOf(':') + 1, dataUrl.indexOf(';'))
        resolve({ base64: base64Data || null, type: mimeType || 'image/jpeg' })
      }
      reader.onerror = () => {
        console.error('FileReader error for image:', imageUrl)
        reject(new Error('FileReader error'))
      }
      reader.readAsDataURL(blob)
    })
  } catch (error) {
    console.error(`Error converting image ${imageUrl} to Base64:`, error)
    return { base64: null, type: null }
  }
}

// URL getter functions specific to Author structure, now part of vCard utils
const getLinkedInUserUrl = (author: Author) => author.linkedInUserId && `https://www.linkedin.com/in/${author.linkedInUserId}/`
const getTwitterUrl = (author: Author) => author.twitterId && `https://x.com/${author.twitterId}/`
const getInstagramUrl = (author: Author) => author.instagramId && `https://www.instagram.com/${author.instagramId}/`
const getFacebookUrl = (author: Author) => author.facebookId && `https://www.facebook.com/${author.facebookId}/`
const getYoutubeUrl = (author: Author) => author.youtubeId && `https://www.youtube.com/@${author.youtubeId}/`

export const generateVCardString = async (author: Author, langCode: LangCode): Promise<string> => {
  const nameParts = author.name.split(' ')
  const lastName = sanitizeVCardTextValue(nameParts.pop() || '') // Sanitize for N
  const firstName = sanitizeVCardTextValue(nameParts.join(' ') || '') // Sanitize for N
  const formattedName = sanitizeVCardTextValue(author.name) // Sanitize for FN
  const title = sanitizeVCardTextValue(author.title[langCode] || author.title[DEFAULT_HYGRAPH_LANG] || Object.values(author.title)[0] || '') // Sanitize for TITLE

  const lines = [
    'BEGIN:VCARD',
    'VERSION:4.0',
    `N:${lastName};${firstName};;;`, // Family Name;Given Name;Additional Names;Prefixes;Suffixes
    `FN:${formattedName}`,
    'ORG:JBDA',
    `TITLE:${title}`
  ]

  // For phone and email, diacritics are not expected.
  // Simple vCard escaping for potential newlines or special chars if any.
  if (author.phone) lines.push(`TEL;TYPE=work,voice:${escapeVCardText(author.phone)}`)
  if (author.email) lines.push(`EMAIL;TYPE=work:${escapeVCardText(author.email)}`)

  if (author.photo?.url) {
    const photoData = await imageToRawBase64(author.photo.url)
    if (photoData.base64) {
      const photoType = photoData.type?.split('/')[1]?.toUpperCase() || 'JPEG'
      lines.push(`PHOTO;ENCODING=BASE64;TYPE=${photoType}:${photoData.base64}`)
    }
  }

  lines.push(`URL;TYPE=home:${buildAuthorUrl(author, langCode)}`)
  const linkedInUrl = getLinkedInUserUrl(author); if (linkedInUrl) lines.push(`URL;TYPE=work:${linkedInUrl}`)
  const twitterUrl = getTwitterUrl(author); if (twitterUrl) lines.push(`URL:${twitterUrl}`)
  const instagramUrl = getInstagramUrl(author); if (instagramUrl) lines.push(`URL:${instagramUrl}`)
  const facebookUrl = getFacebookUrl(author); if (facebookUrl) lines.push(`URL:${facebookUrl}`)
  const youtubeUrl = getYoutubeUrl(author); if (youtubeUrl) lines.push(`URL:${youtubeUrl}`)

  lines.push('NOTE:Legibloq')
  lines.push('END:VCARD')
  return lines.join('\n')
}
